import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, User, Lock, Building2, Mail, Phone, MapPin, Globe, ArrowLeft, ArrowRight } from 'lucide-react';

const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    name: '',
    email: '',
    company_name: '',
    whatsapp: '',
    address: '',
    city: '',
    state: '',
    website: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    
    try {
      await register(formData);
      toast.success('Registration successful! Pending admin approval.');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Hero */}
      <div className="hidden lg:flex w-2/5 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/2007395/pexels-photo-2007395.jpeg"
          alt="Travel"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-slate-900/90"></div>
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center text-white font-bold text-xl backdrop-blur">
              S
            </div>
            <div className="leading-none">
              <span className="block text-lg font-bold">Suvidha Travel</span>
              <span className="block text-xs text-white/70">Your B2B Partner</span>
            </div>
          </Link>

          <div>
            <h2 className="text-4xl font-bold mb-4">Join Our Network</h2>
            <p className="text-lg text-white/80 mb-8">
              Register as a B2B agent and unlock access to wholesale rates across India.
            </p>
          </div>

          <div className="text-sm text-white/60">
            <p>Established 2011 • Delhi NCR, India</p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white overflow-y-auto">
        <div className="w-full max-w-xl">
          <div className="mb-8">
            <Link to="/login" className="inline-flex items-center gap-2 text-slate-500 hover:text-primary mb-4 text-sm">
              <ArrowLeft size={16} /> Back to Login
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
              Agent Onboarding
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Register Account</h1>
            <p className="text-slate-500">Join our network of 1500+ verified travel agents.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Manager Name *</Label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    name="name"
                    data-testid="register-name"
                    placeholder="John Doe"
                    className="pl-10 h-11"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Agency Name *</Label>
                <div className="relative">
                  <Building2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    name="company_name"
                    data-testid="register-company"
                    placeholder="Global Travels"
                    className="pl-10 h-11"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">WhatsApp *</Label>
                <div className="relative">
                  <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    name="whatsapp"
                    data-testid="register-whatsapp"
                    placeholder="+91 987..."
                    className="pl-10 h-11"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email *</Label>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    name="email"
                    type="email"
                    data-testid="register-email"
                    placeholder="mail@agency.com"
                    className="pl-10 h-11"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Office Address</Label>
              <div className="relative">
                <MapPin size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  name="address"
                  data-testid="register-address"
                  placeholder="Suite, Building, Locality"
                  className="pl-10 h-11"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">City</Label>
                <Input
                  name="city"
                  data-testid="register-city"
                  placeholder="Mumbai"
                  className="h-11"
                  value={formData.city}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">State</Label>
                <Input
                  name="state"
                  data-testid="register-state"
                  placeholder="Maharashtra"
                  className="h-11"
                  value={formData.state}
                  onChange={handleChange}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Website</Label>
                <Input
                  name="website"
                  data-testid="register-website"
                  placeholder="www.agency.com"
                  className="h-11"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="border-t pt-5 space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Username *</Label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    name="username"
                    data-testid="register-username"
                    placeholder="Choose a username"
                    className="pl-10 h-11"
                    value={formData.username}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password *</Label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      name="password"
                      type="password"
                      data-testid="register-password"
                      placeholder="••••••••"
                      className="pl-10 h-11"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Confirm Password *</Label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      name="confirmPassword"
                      type="password"
                      data-testid="register-confirm-password"
                      placeholder="••••••••"
                      className="pl-10 h-11"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? (
                <Loader2 className="animate-spin mr-2" size={20} />
              ) : (
                <>Create Account <ArrowRight size={18} className="ml-2" /></>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-500">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-semibold hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
